import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, ChevronsUpDown, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { getAuthHeaders } from '../../contexts/AuthContext';
import { money, number } from './format';
import { platformLabel } from '../../config/platforms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MATCH_TYPES = [
  ['master', 'Master code'],
  ['old_code', 'Old code'],
  ['identifier', 'ASIN / FSN / seller SKU'],
  ['mapped', 'Mapped now'],
];

export const downloadUnmatched = async (codes, source) => {
  try {
    const res = await axios.post(`${API}/sku-aliases/unmatched-export`, { codes, source }, { headers: getAuthHeaders(), responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `unmatched_codes_${source}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch {
    toast.error('Could not download unmatched codes');
  }
};

export const UnmatchedExportButton = ({ codes, source }) => (
  <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadUnmatched(codes, source)} data-testid={`unmatched-export-${source}`}>
    <Download className="mr-1 h-3.5 w-3.5" />Download unmatched codes
  </Button>
);

export const MatchTypeCounts = ({ counts }) => {
  if (!counts) return null;
  const shown = MATCH_TYPES.filter(([key]) => counts[key] > 0);
  if (!shown.length) return null;
  return (
    <div className="flex flex-wrap gap-2 text-xs" data-testid="sales-match-counts">
      {shown.map(([key, label]) => (
        <span key={key} className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-700" data-testid={`sales-match-count-${key}`}>
          {label}: <span className="font-semibold tabular-nums">{number(counts[key])}</span>
        </span>
      ))}
    </div>
  );
};

export const MatchBadge = ({ row }) => {
  if (!row.match_type || row.match_type === 'master') return null;
  return (
    <span className="mt-0.5 block text-[10px] text-gray-500" data-testid={`sales-row-match-${row.row_num}`}>
      {row.match_label} · {row.source_code}
    </span>
  );
};

export const useSkuOptions = (country) => {
  const [skus, setSkus] = useState([]);
  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${API}/skus`, { params: { country }, headers: getAuthHeaders(), signal: controller.signal })
      .then((res) => { if (!controller.signal.aborted) setSkus(res.data); })
      .catch(() => { if (!controller.signal.aborted) setSkus([]); });
    return () => controller.abort();
  }, [country]);
  return skus;
};

export const SkuPicker = ({ code, value, skus, onPick, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = useMemo(() => skus.find((s) => s.sku_code === value), [skus, value]);
  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    return skus.filter((s) => !term || `${s.sku_code} ${s.product_name || ''}`.toLowerCase().includes(term)).slice(0, 50);
  }, [skus, search]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} className="h-8 w-64 justify-between font-mono text-xs" data-testid={`sales-map-picker-${code}`}>
          <span className="truncate">{selected ? selected.sku_code : 'Pick SKU or bundle…'}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={search} onValueChange={setSearch} placeholder="Search SKU code or product…" data-testid={`sales-map-search-${code}`} />
          <CommandList className="max-h-64">
            <CommandEmpty>No SKU found.</CommandEmpty>
            <CommandGroup>
              {results.map((s) => (
                <CommandItem key={s.sku_code} value={`${s.sku_code} ${s.product_name || ''}`} onSelect={() => { onPick(s.sku_code); setOpen(false); }} data-testid={`sales-map-option-${s.sku_code}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-xs">{s.sku_code}</div>
                    <div className="truncate text-[11px] text-gray-500">{s.product_name}</div>
                    {s.bundle_of && <div className="text-[10px] font-semibold text-emerald-700">Bundle · {s.bundle_qty} × {s.bundle_of}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const UnmatchedCodesPanel = ({ codes, mappings, onChange, country, disabled }) => {
  const skus = useSkuOptions(country);
  const pending = Object.keys(mappings || {});
  if (!codes?.length && !pending.length) return null;
  const setMapping = (code, sku) => {
    const next = { ...mappings };
    if (sku) next[code] = sku; else delete next[code];
    onChange(next);
  };
  return (
    <div className={`space-y-2 rounded-lg border p-3 ${codes.length ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}`} data-testid="sales-unmatched-panel">
      <div className={`flex items-center gap-2 text-sm font-semibold ${codes.length ? 'text-amber-900' : 'text-emerald-800'}`}>
        {codes.length ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
        <span className="flex-1">{codes.length ? `${codes.length} code(s) not found — map them or their rows are skipped` : 'All codes mapped'}</span>
        {codes.length > 0 && <UnmatchedExportButton codes={codes} source="sales" />}
      </div>
      {codes.map((item) => (
        <div key={item.code} className="flex flex-wrap items-center gap-3 rounded-md bg-white p-2 text-xs" data-testid={`sales-unmatched-${item.code}`}>
          <span className="min-w-[140px] font-mono font-semibold">{item.code}</span>
          <span className="text-gray-500">{number(item.rows)} rows · {number(item.units)} units · {item.platforms.join(', ')}</span>
          <SkuPicker code={item.code} value={mappings?.[item.code]} skus={skus} onPick={(sku) => setMapping(item.code, sku)} disabled={disabled} />
        </div>
      ))}
      {pending.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="sales-mapped-list">
          {pending.map((code) => (
            <span key={code} className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-800">
              <span className="font-mono">{code} → {mappings[code]}</span>
              <button type="button" aria-label={`Remove mapping ${code}`} onClick={() => setMapping(code, null)} disabled={disabled} data-testid={`sales-mapped-remove-${code}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
          <span className="text-[11px] text-gray-500">Saved for future uploads when you confirm.</span>
        </div>
      )}
    </div>
  );
};

export const PriceFlag = ({ row }) => {
  if (!row.price_flags?.length) return null;
  return <span className="mt-0.5 block text-[10px] font-medium text-amber-700" title={row.price_flags.join(' · ')} data-testid={`sales-row-price-flag-${row.row_num}`}>Unusual price</span>;
};

export const PriceFlagsPanel = ({ flags, total }) => {
  if (!flags?.length) return null;
  return (
    <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="sales-price-flags-panel">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="h-4 w-4" />{number(total || flags.length)} row(s) with unusual pricing — check before importing{total > flags.length ? ` (first ${flags.length} shown)` : ''}
      </div>
      <div className="max-h-48 overflow-auto rounded-md bg-white">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-gray-50 text-left text-gray-500">
            <tr><th className="p-1.5">Row</th><th className="p-1.5">SKU</th><th className="p-1.5">Platform</th><th className="p-1.5 text-right">Units</th><th className="p-1.5 text-right">Sale value</th><th className="p-1.5 text-right">Per unit</th><th className="p-1.5 text-right">SKU price</th><th className="p-1.5">Why</th></tr>
          </thead>
          <tbody>
            {flags.map((f) => (
              <tr key={f.row_num} className="border-t" data-testid={`sales-price-flag-${f.row_num}`}>
                <td className="p-1.5 text-gray-400">{f.row_num}</td>
                <td className="p-1.5 font-mono">{f.sku_code}</td>
                <td className="p-1.5">{platformLabel(f.platform)}</td>
                <td className="p-1.5 text-right">{number(f.units)}</td>
                <td className="p-1.5 text-right">{money(f.sale_value)}</td>
                <td className="p-1.5 text-right">{money(f.unit_price)}</td>
                <td className="p-1.5 text-right">{money(f.selling_price)}</td>
                <td className="p-1.5 text-amber-800">{f.reasons.join(' · ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const ReportInfoBanner = ({ info }) => {
  if (!info) return null;
  const skipped = [
    info.cancelled_skipped && `${number(info.cancelled_skipped)} cancelled / returned`,
    info.zero_qty_skipped && `${number(info.zero_qty_skipped)} with 0 quantity`,
    info.bad_date_skipped && `${number(info.bad_date_skipped)} without a valid date`,
  ].filter(Boolean);
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900" data-testid="sales-report-info">
      <span className="font-semibold">Detected: {info.format}.</span> {number(info.order_lines)} order lines read, {number(info.sales_rows)} counted as sales
      {skipped.length > 0 && <> · skipped {skipped.join(', ')}</>}. Dates use the order date (IST); value is the full invoice amount incl. shipping.
    </div>
  );
};
